class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;

    // Timestamp of the last open, used to ignore clicks that race the
    // auto-open after an add-to-cart (they land on the overlay/trigger
    // and would instantly toggle the drawer closed).
    this.lastOpenedAt = 0;

    // Bound handlers so listeners on shared elements (header trigger,
    // document) can be removed when this element is replaced by an app
    // re-render — stale listeners double-toggle and break the drawer.
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onRefreshEvent = this.onRefreshEvent.bind(this);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", this.onRefreshEvent);
      return;
    }

    this.init();

    // If an app re-render (e.g. AppStle merging cart lines) replaced this
    // element while the drawer was open, the fresh HTML arrives closed but
    // the body overlay class survives the swap — restore the open state
    // instead of snapping shut.
    if (
      this.body.classList.contains(this.activeOverlayBodyClass) &&
      !this.isOpen
    ) {
      this.isOpen = true;
      this.lastOpenedAt = Date.now();
      this.setAttribute("open", "");
      this.getDrawer().classList.add(this.classDrawerActive);
      setTabindex(this.toggleEelements(), "0");
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    document.removeEventListener("cart-drawer:refresh", this.onRefreshEvent);
    this.triggers().forEach((trigger) =>
      trigger.removeEventListener("click", this.onTriggerClick),
    );
  }

  rememberOpener(e) {
    // Prefer currentTarget (the element with listener), fallback to activeElement
    const current = e?.currentTarget;
    const active = document.activeElement;

    // If click landed on svg/span inside button, climb up to interactive parent
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;

    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;

    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;

    // Wait one frame so DOM/classes settle after closing
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");

      // Restore focus to the element that opened the drawer
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.lastOpenedAt = Date.now();
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  getDrawer() {
    // Re-query on use: app re-renders can replace the drawer node, leaving
    // a cached reference detached.
    const current = this.querySelector(`.${this.drawerClass}`);
    if (current) this.drawer = current;
    return this.drawer;
  }

  toggleDrawerClasses() {
    this.onToggle();
    // Force the classes to match the state instead of blind-toggling, so a
    // desynced class can never invert the open/close behavior.
    this.getDrawer().classList.toggle(this.classDrawerActive, this.isOpen);
    this.body.classList.toggle(this.activeOverlayBodyClass, this.isOpen);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", this.onTriggerClick);
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", this.onRefreshEvent);
  }

  onTriggerClick(e) {
    e.preventDefault();

    const trigger = e.currentTarget;

    // Scripted clicks (element.click() from apps or theme code) carry
    // intent: the header trigger means "show the drawer", the overlay and
    // back-link mean "hide it". Toggling on these lets a second "show"
    // close a drawer that just opened.
    if (!e.isTrusted) {
      const wantsOpen = trigger.classList.contains("wt-cart__trigger");
      if (wantsOpen !== this.isOpen) {
        if (wantsOpen) this.rememberOpener(e);
        this.toggleDrawerClasses();
      }
      return;
    }

    // A real click landing right as the drawer auto-opens (double-click
    // on a CTA, or clicking the cart icon while an add is in flight)
    // hits the overlay/trigger and would instantly close it — ignore
    // closes inside this short grace window.
    if (this.isOpen && Date.now() - this.lastOpenedAt < 500) return;

    // Save opener only when opening
    if (!this.isOpen) this.rememberOpener(e);

    this.toggleDrawerClasses();
  }

  onRefreshEvent(e) {
    this.refreshCartDrawer(e);
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        // Open only if not already open — never toggle-close a visible drawer.
        if (!this.isOpen) this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((e) => {
        console.log(e);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
